import React, { useState, useEffect } from 'react';
import { timeEntryService } from '../../services/timeEntry.service';
import { TimeEntryStatusResponse } from '../../types/timeEntry.types';
import styles from './CheckInOut.module.css';

export const CheckInOut: React.FC = () => {
  const [status, setStatus] = useState<TimeEntryStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [note, setNote] = useState('');
  const [showNoteInput, setShowNoteInput] = useState(false);

  const loadStatus = async () => {
    try {
      setLoading(true);
      const data = await timeEntryService.getStatus();
      setStatus(data);
    } catch (err: unknown) {
      console.error('Error loading status:', err);
      const message = err instanceof Error ? err.message : 'Errore nel caricamento dello stato';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const handleCheckIn = async () => {
    setProcessing(true);
    setError('');
    setSuccess('');

    try {
      // Prova a ottenere le coordinate GPS (opzionale)
      let gpsCoordinates: string | undefined;

      if (navigator.geolocation) {
        try {
          const position = await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
          });
          gpsCoordinates = `${position.coords.latitude},${position.coords.longitude}`;
        } catch (gpsError) {
          console.log('GPS non disponibile, continuo senza coordinate');
        }
      }

      await timeEntryService.checkIn({
        gpsCoordinates,
        notes: note || undefined
      });

      setSuccess('✅ Check-in effettuato con successo!');
      setNote('');
      setShowNoteInput(false);
      await loadStatus();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Errore durante il check-in';
      setError(message);
    } finally {
      setProcessing(false);
    }
  };

  const handleCheckOut = async () => {
    setProcessing(true);
    setError('');
    setSuccess('');

    try {
      // Prova a ottenere le coordinate GPS (opzionale)
      let gpsCoordinates: string | undefined;

      if (navigator.geolocation) {
        try {
          const position = await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
          });
          gpsCoordinates = `${position.coords.latitude},${position.coords.longitude}`;
        } catch (gpsError) {
          console.log('GPS non disponibile, continuo senza coordinate');
        }
      }

      await timeEntryService.checkOut({
        gpsCoordinates,
        notes: note || undefined
      });

      setSuccess('✅ Check-out effettuato con successo!');
      setNote('');
      setShowNoteInput(false);
      await loadStatus();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Errore durante il check-out';
      setError(message);
    } finally {
      setProcessing(false);
    }
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('it-IT', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Caricamento...</div>
      </div>
    );
  }

  const isCheckedIn = status?.isCheckedIn || false;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>🕒 Timbratura</h2>
        {status?.lastEntry && (
          <div className={styles.lastEntry}>
            Ultimo: {isCheckedIn ? '✅ Entrata' : '❌ Uscita'} alle {formatTime(status.lastEntry.timestamp)}
          </div>
        )}
      </div>

      {error && (
        <div className={styles.error}>
          ❌ {error}
        </div>
      )}

      {success && (
        <div className={styles.success}>
          {success}
        </div>
      )}

      <div className={styles.statusCard}>
        <div className={`${styles.statusIndicator} ${isCheckedIn ? styles.checkedIn : styles.checkedOut}`}>
          <div className={styles.statusIcon}>
            {isCheckedIn ? '🟢' : '🔴'}
          </div>
          <div className={styles.statusText}>
            <div className={styles.statusLabel}>Stato Attuale</div>
            <div className={styles.statusValue}>
              {isCheckedIn ? 'IN SEDE' : 'FUORI SEDE'}
            </div>
            {status?.lastEntry && (
              <div className={styles.statusTime}>
                {formatDate(status.lastEntry.timestamp)} - {formatTime(status.lastEntry.timestamp)}
              </div>
            )}
          </div>
        </div>

        {showNoteInput && (
          <div className={styles.noteSection}>
            <textarea
              className={styles.noteInput}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Aggiungi una nota (opzionale)"
              rows={2}
            />
          </div>
        )}

        <div className={styles.actions}>
          {!showNoteInput && (
            <button
              className={styles.noteButton}
              onClick={() => setShowNoteInput(true)}
              disabled={processing}
            >
              📝 Aggiungi Nota
            </button>
          )}

          {showNoteInput && (
            <button
              className={styles.cancelNoteButton}
              onClick={() => {
                setShowNoteInput(false);
                setNote('');
              }}
              disabled={processing}
            >
              ❌ Annulla Nota
            </button>
          )}

          {isCheckedIn ? (
            <button
              className={`${styles.button} ${styles.checkOutButton}`}
              onClick={handleCheckOut}
              disabled={processing}
            >
              {processing ? '⏳ Elaborazione...' : '🔓 Check-Out'}
            </button>
          ) : (
            <button
              className={`${styles.button} ${styles.checkInButton}`}
              onClick={handleCheckIn}
              disabled={processing}
            >
              {processing ? '⏳ Elaborazione...' : '🔒 Check-In'}
            </button>
          )}
        </div>
      </div>

      <div className={styles.info}>
        💡 <strong>Suggerimento:</strong> Assicurati di essere in sede quando effettui la timbratura.
        {navigator.geolocation && ' La tua posizione GPS verrà registrata automaticamente.'}
      </div>
    </div>
  );
};

CheckInOut.displayName = 'CheckInOut';
